import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
const _excluded = ["makeStyles", "styles", "messages", "isGroup"];
var _dec, _dec2, _class, _FormFieldLayout;
/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/** @jsx jsx */
import { Component } from 'react';
import { hasVisibleChildren } from '@instructure/ui-a11y-utils';
import { omitProps, getElementType, withDeterministicId } from '@instructure/ui-react-utils';
import { withStyle, jsx } from '@instructure/emotion';
import { FormFieldMessages } from '../FormFieldMessages';
import generateStyle from './styles';
import { propTypes, allowedProps } from './props';
import generateComponentTheme from './theme';

/**
---
parent: FormField
---
**/
let FormFieldLayout = (_dec = withDeterministicId(), _dec2 = withStyle(generateStyle, generateComponentTheme), _dec(_class = _dec2(_class = (_FormFieldLayout = class FormFieldLayout extends Component {
  constructor(props) {
    super(props);
    this._messagesId = void 0;
    this._labelId = void 0;
    this.ref = null;
    this.handleRef = el => {
      const elementRef = this.props.elementRef;
      this.ref = el;
      if (typeof elementRef === 'function') {
        elementRef(el);
      }
    };
    this.makeStyleProps = () => {
      var _this$props$messages;
      const hasNewErrorMsgAndIsGroup = !!((_this$props$messages = this.props.messages) !== null && _this$props$messages !== void 0 && _this$props$messages.find(m => m.type === 'newError')) && !!this.props.isGroup;
      return {
        hasMessages: this.hasMessages,
        hasVisibleLabel: this.hasVisibleLabel,
        // if true render error message above the controls (and below the label)
        hasNewErrorMsgAndIsGroup: hasNewErrorMsgAndIsGroup
      };
    };
    this.handleInputContainerRef = node => {
      if (typeof this.props.inputContainerRef === 'function') {
        this.props.inputContainerRef(node);
      }
    };
    this._messagesId = props.messagesId || props.deterministicId();
    this._labelId = props.deterministicId('FormField-Label');
  }
  componentDidMount() {
    var _this$props$makeStyle, _this$props;
    (_this$props$makeStyle = (_this$props = this.props).makeStyles) === null || _this$props$makeStyle === void 0 ? void 0 : _this$props$makeStyle.call(_this$props, this.makeStyleProps());
  }
  componentDidUpdate() {
    var _this$props$makeStyle2, _this$props2;
    (_this$props$makeStyle2 = (_this$props2 = this.props).makeStyles) === null || _this$props$makeStyle2 === void 0 ? void 0 : _this$props$makeStyle2.call(_this$props2, this.makeStyleProps());
  }
  get hasVisibleLabel() {
    return this.props.label ? hasVisibleChildren(this.props.label) : false;
  }
  get hasMessages() {
    if (!this.props.messages || this.props.messages.length == 0) {
      return false;
    }
    for (const msg of this.props.messages) {
      if (msg.text) {
        if (typeof msg.text === 'string') {
          return msg.text.length > 0;
        }
        // this is more complicated (e.g. an array, a React component,...)
        // but we don't try to optimize here for these cases
        return true;
      }
    }
    return false;
  }
  get elementType() {
    return getElementType(FormFieldLayout, this.props);
  }
  renderLabel() {
    if (this.hasVisibleLabel) {
      var _this$props$styles2;
      if (this.elementType == 'fieldset') {
        var _this$props$styles;
        // `legend` has some special built in CSS, this can only be reset
        // this way https://stackoverflow.com/a/65866981/319473
        return jsx("legend", {
          style: {
            display: 'contents'
          }
        }, jsx("span", {
          css: (_this$props$styles = this.props.styles) === null || _this$props$styles === void 0 ? void 0 : _this$props$styles.formFieldLabel
        }, this.props.label));
      }
      return jsx("span", {
        css: (_this$props$styles2 = this.props.styles) === null || _this$props$styles2 === void 0 ? void 0 : _this$props$styles2.formFieldLabel
      }, this.props.label);
    } else if (this.props.label) {
      if (this.elementType == 'fieldset') {
        return jsx("legend", {
          id: this._labelId,
          style: {
            display: 'contents'
          }
        }, this.props.label);
      }
      // needs to be wrapped because it needs an `id`
      return jsx("div", {
        id: this._labelId,
        style: {
          display: 'contents'
        }
      }, this.props.label);
    } else return null;
  }
  renderVisibleMessages() {
    return this.hasMessages ? jsx(FormFieldMessages, {
      id: this._messagesId,
      messages: this.props.messages,
      gridArea: "messages"
    }) : null;
  }
  render() {
    // Should be `<label>` if it's a FormField, fieldset if it's a group
    const ElementType = this.elementType;
    const _this$props3 = this.props,
      makeStyles = _this$props3.makeStyles,
      styles = _this$props3.styles,
      messages = _this$props3.messages,
      isGroup = _this$props3.isGroup,
      props = _objectWithoutProperties(_this$props3, _excluded);
    const width = props.width,
      children = props.children;
    const hasNewErrorMsgAndIsGroup = !!(messages !== null && messages !== void 0 && messages.find(m => m.type === 'newError')) && isGroup;
    return jsx(ElementType, Object.assign({}, omitProps(props, [...FormFieldLayout.allowedProps]), {
      css: styles === null || styles === void 0 ? void 0 : styles.formFieldLayout,
      "aria-describedby": this.hasMessages ? this._messagesId : void 0,
      "aria-errormessage": this.props['aria-invalid'] ? this._messagesId : void 0,
      style: {
        width
      },
      ref: this.handleRef
    }), this.renderLabel(), hasNewErrorMsgAndIsGroup && this.renderVisibleMessages(), jsx("span", {
      css: styles === null || styles === void 0 ? void 0 : styles.formFieldChildren,
      ref: this.handleInputContainerRef
    }, children), !hasNewErrorMsgAndIsGroup && this.renderVisibleMessages());
  }
}, _FormFieldLayout.displayName = "FormFieldLayout", _FormFieldLayout.componentId = 'FormFieldLayout', _FormFieldLayout.propTypes = propTypes, _FormFieldLayout.allowedProps = allowedProps, _FormFieldLayout.defaultProps = {
  inline: false,
  layout: 'stacked',
  as: 'label',
  labelAlign: 'end'
}, _FormFieldLayout)) || _class) || _class);
export default FormFieldLayout;
export { FormFieldLayout };